import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import {
  login as apiLogin,
  logout as apiLogout,
  fetchLoginInfo,
  fetchUserPackageStatus,
  createUser,
  getAccessToken,
  setAccessToken,
  clearAccessToken,
} from "@/lib/api";
import { normalizePhoneAZ } from "@/lib/format";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(undefined);
  const [token, setToken] = useState(() => getAccessToken());
  const [isPackageActive, setIsPackageActive] = useState(false);
  const [packageExpireDate, setPackageExpireDate] = useState(null);

  useEffect(() => {
    const onSessionExpired = () => {
      setUser(null);
      setToken(null);
      setIsPackageActive(false);
      setPackageExpireDate(null);
    };

    const onTokenRefreshed = () => {
      setToken(getAccessToken());
    };

    const onStorage = (e) => {
      if (e.key === "emlak_access_token") {
        setToken(e.newValue || null);
      }
    };

    window.addEventListener("auth:session-expired", onSessionExpired);
    window.addEventListener("auth:token-refreshed", onTokenRefreshed);
    window.addEventListener("storage", onStorage);

    return () => {
      window.removeEventListener("auth:session-expired", onSessionExpired);
      window.removeEventListener("auth:token-refreshed", onTokenRefreshed);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const existingViewMode = localStorage.getItem('viewMode');
    if (existingViewMode === null) {
      const initialViewMode = window.innerWidth < 768 ? '1' : '0';
      localStorage.setItem('viewMode', initialViewMode);
    }

    (async () => {
      if (!token) {
        setUser(null);
        setIsPackageActive(false);
        setPackageExpireDate(null);
        return;
      }

      try {
        const data = await fetchLoginInfo();
        if (!cancelled) {
          setUser(data.user);

          if (data.user?.viewMode !== undefined && data.user?.viewMode !== null) {
            localStorage.setItem('viewMode', String(data.user.viewMode));
          }
        }
      } catch {
        if (!cancelled) {
          setUser(null);
          setToken(null);
          setIsPackageActive(false);
          setPackageExpireDate(null);
        }
        return;
      }

      try {
        const pkg = await fetchUserPackageStatus();
        if (!cancelled) {
          setIsPackageActive(Boolean(pkg?.isActive));
          setPackageExpireDate(pkg?.expireDate || null);
        }
      } catch {
        if (!cancelled) {
          setIsPackageActive(false);
          setPackageExpireDate(null);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [token]);

  const login = useCallback(async (phoneNumberRaw, password) => {
    const phoneNumber = normalizePhoneAZ(phoneNumberRaw);
    const data = await apiLogin(phoneNumber, password);
    setAccessToken(data.accessToken);
    setToken(data.accessToken);
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (phoneNumberRaw, referralId) => {
    const phoneNumber = normalizePhoneAZ(phoneNumberRaw);
    return createUser(phoneNumber, referralId || null);
  }, []);

  const logout = useCallback(() => {
    apiLogout().catch(() => { });
    clearAccessToken();
    setToken(null);
    setUser(null);
    setIsPackageActive(false);
    setPackageExpireDate(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      token,
      login,
      register,
      logout,
      loading: user === undefined,
      isPackageActive,
      hasActivePackage: isPackageActive,
      packageExpireDate,
    }),
    [user, token, login, register, logout, isPackageActive, packageExpireDate],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
};

export const formatApiError = (detail) => {
  if (detail == null) return "Xəta baş verdi.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};